import { BusinessObjectBase } from './base';
import { sessionHistorySchema } from '@schemas/business-objects/session-history';
import type { Student } from './student';
import type { AttendanceType, CalendarDate, Id } from '../base-types';

export interface SessionHistoryData {
  readonly id: Id;
  readonly date?: CalendarDate;
  readonly attendanceType?: AttendanceType;
  readonly studentId: Id;
  readonly student?: Student;
}

export interface SessionHistory extends Readonly<SessionHistoryData> {}
export class SessionHistory extends BusinessObjectBase {
  static readonly entityName = 'SessionHistory' as const;
  static readonly plural = 'session-histories';
  static schema = sessionHistorySchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<SessionHistory[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new SessionHistory(data));
  }

  static async findOne(filter?: any): Promise<SessionHistory | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new SessionHistory(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<SessionHistory> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new SessionHistory(data);
  }

  static async create(
    data: Omit<SessionHistoryData, 'id'>,
  ): Promise<SessionHistory> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new SessionHistory(result);
  }

  static async createMany(
    data: Omit<SessionHistoryData, 'id'>[],
  ): Promise<SessionHistory[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new SessionHistory(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<SessionHistoryData>,
  ): Promise<SessionHistory> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new SessionHistory(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
